import React, { FC, useMemo, useCallback } from 'react';
import { Field } from './CompanyFieldAndHelpers';
import { Grid, Column, Item, Spacer } from './CompanyGrid';
import TrendingUpIcon from 'mdi-react/TrendingUpIcon';
import TrendingDownIcon from 'mdi-react/TrendingDownIcon';
import MinusIcon from 'mdi-react/MinusIcon';
import styled from 'styled-components';
import { WWWCompanyDocument } from '../../../common/documents/WWWCompanyDocument';
import { LineGraph } from './LineGraph';
import { BarGraph } from './BarGraph';
import { useFinancialData } from './useFinancialData';
import { formatNumber } from '../../lib/utils';
import { Color } from '../Theme';

const State = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`;

const Trend = styled.div<{ color: string; notFinite: boolean }>`
  display: flex;
  align-items: center;
  color: ${({ color }) => color};

  svg {
    opacity: 0.7;
  }

  b {
    // font-weight: 500;

    // hide % if not finite value
    ${({ notFinite }) =>
      notFinite
        ? ``
        : `
        &:after {
          content: '%';
          font-size: 1.2rem;
          opacity: 0.7;
        }
        `}
  }
`;

const TrendingValue: FC<{ value: number; size?: number }> = ({ value, size = 28 }) => {
  const color = useMemo(() => {
    if (value > 0) return Color.green;
    if (value < 0) return Color.red;
    return Color.disabled;
  }, []);

  const icon = useMemo(() => {
    if (value > 0) return <TrendingUpIcon />;
    if (value < 0) return <TrendingDownIcon />;
    return <MinusIcon />;
  }, [value]);

  return (
    <Trend color={color} notFinite={!isFinite(value)}>
      {<icon.type size={size} />}{' '}
      <b>
        {value > 0 && '+'}
        {(value === 0 || isNaN(value)) && '±'}
        {value ? (isFinite(value) ? formatNumber(parseFloat(value.toFixed(1))) : '') : 0}
      </b>
    </Trend>
  );
};

interface Props {
  company: WWWCompanyDocument;
  editable: boolean;
  premium: boolean;
  loading: boolean;
}

export const CompanyFinanceInfo: FC<Props> = ({ company, premium, loading }) => {
  const {
    turnoverData,
    turnoverChange,
    turnoverScale,
    operatingData,
    operatingChange,
    operatingScale,
    equityData,
    equityChange,
    employeeData,
    employeeChange,
  } = useFinancialData(company);

  const formatEquityTooltip = useCallback(
    (value: number) => (
      <>
        {formatNumber(value * 100)}
        <span style={{ fontSize: '0.75em', opacity: 0.5 }}>%</span>
      </>
    ),
    []
  );

  return (
    <Grid>
      <Column>
        {!!turnoverData.length && (
          <Item>
            <Field label="Liikevaihto" empty={!turnoverData.length} large>
              <State>
                {formatNumber(turnoverData[0].value)}
                <TrendingValue value={turnoverChange} />
              </State>
            </Field>
            <LineGraph data={turnoverData} scale={turnoverScale} />
          </Item>
        )}
        <Spacer />
        {!!equityData.length && (
          <Item>
            <Field label="Omavaraisuus" empty={!equityData.length} large>
              <State>
                {(equityData[0].value * 100).toFixed(0)} %
                <TrendingValue value={equityChange} />
              </State>
            </Field>
            <BarGraph data={equityData} scale={[0, 1]} formatTooltip={formatEquityTooltip} />
          </Item>
        )}
      </Column>
      <Column>
        {!!operatingData.length && (
          <Item>
            <Field label="Liikevoitto" empty={!operatingData.length} large>
              <State>
                {formatNumber(operatingData[0].value)}
                <TrendingValue value={operatingChange} />
              </State>
            </Field>
            <LineGraph data={operatingData} scale={operatingScale} />
          </Item>
        )}
        <Spacer />
        {!!employeeData.length && (
          <Item>
            <Field label="Henkilöstö" empty={!employeeData.length} large>
              <State>
                {formatNumber(employeeData[0].value)}
                <TrendingValue value={employeeChange} />
              </State>
            </Field>
            <BarGraph data={employeeData} />
          </Item>
        )}
      </Column>
    </Grid>
  );
};
